import React, { useLayoutEffect, useReducer, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import { KeyboardArrowDown } from '@mui/icons-material';
import { Avatar, Box, Button, Paper, Typography } from '@mui/material';

import isEmpty from 'utils/is-empty';
import Utils from 'utils/Utils';

const POST_WIDTH = 250;
const POST_GAP = 24;

const useStyles = makeStyles()((theme) => ({
    container: {
        paddingBottom: 20
    },
    posts: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: POST_GAP
    },
    head: {
        marginTop: 60,
        marginBottom: 20,
        fontSize: 24,
        fontWeight: 700
    },
    card: {
        display: 'flex',
        flexDirection: 'column',
        padding: 20,
        width: POST_WIDTH,
        border: '1px solid #F5F5F5'
    },
    author: {
        fontWeight: 600,
        marginLeft: 12
    },
    text: {
        marginTop: 12,
        marginBottom: 20,
        wordBreak: 'break-word'
    },
    link: {
        marginTop: 'auto',
        marginRight: 'auto',
        background: '#FAFAFA',
        borderRadius: 8,
        boxShadow: 'none',
        textTransform: 'none',
        color: '#979797',
        '&:hover': {
            background: '#efefef',
            boxShadow: 'none'
        }
    },
    more: {
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        marginTop: 24,
        '&>p, >svg': {
            fontWeight: 600,
            color: theme.palette.primary.main
        }
    }
}));

interface Props {
    posts: {
        author?: string;
        author_photo?: string;
        link: string;
        text: string;
    }[];
}

const SocialReferences: React.FC<Props> = ({ posts }) => {
    const { classes } = useStyles();
    const [open, toggleOpen] = useReducer((state) => !state, false);
    const [inLineCount, setInLineCount] = useState(0);

    const containerRef = useRef<HTMLDivElement | null>(null);

    useLayoutEffect(() => {
        const onResize = () => {
            if (containerRef.current) {
                const width = containerRef.current.clientWidth;
                const inLineCount = Math.floor((width + POST_GAP) / (POST_WIDTH + POST_GAP));
                setInLineCount(inLineCount);
            }
        };
        const debounced = Utils.debounce(onResize, 250);
        window.addEventListener('resize', debounced);
        onResize();
        return () => window.removeEventListener('resize', debounced);
    }, []);

    if (isEmpty(posts)) {
        return null;
    }

    const isLong = posts.length > inLineCount;
    const slice = open ? posts : posts.slice(0, inLineCount);

    return (
        <Box className={classes.container}>
            <Typography className={classes.head} variant="h2">
                {'Social References'}
            </Typography>

            <div className={classes.posts} ref={containerRef}>
                {slice.map(({ author, author_photo, link, text }, index) => (
                    <Paper key={index} className={classes.card} elevation={0}>
                        <Box display="flex" alignItems="center">
                            <Avatar src={author_photo} />
                            <Typography variant="body2" className={classes.author}>
                                {author}
                            </Typography>
                        </Box>

                        <Box className={classes.text}>
                            <Typography variant="body2">
                                {text.replaceAll(
                                    /\/hashtag\/([A-Za-z0-9-_]+)\?src=hashtag_click/gi,
                                    (_, match) => `#${match}`
                                )}
                            </Typography>
                        </Box>

                        <Button
                            className={classes.link}
                            variant="contained"
                            href={link}
                            target="_blank"
                            rel="nofollow noreferrer noopener"
                        >
                            {new URL(link).hostname}
                        </Button>
                    </Paper>
                ))}
            </div>

            {isLong && (
                <div className={classes.more} onClick={toggleOpen}>
                    <Typography variant="body2">{open ? 'Less' : 'More'}</Typography>
                    <KeyboardArrowDown style={{ transform: open ? 'rotate(-180deg)' : 'initial' }} />
                </div>
            )}
        </Box>
    );
};

export default SocialReferences;
